// playbookFocusUtils.js

import { MISSION_TASK_HANDLED_BY } from 'missions/taskmodal/missionTaskModalUtils';
import { getMissionActivePathTasks } from 'node/missions/mission/utils/missionRunUtils';

const ACTIONABLE_STEP_STATUSES = new Set([
  'blocked',
  'failed',
  'inProgress',
  'ready',
  'running',
]);

/**
 * isPlaybookFocusStepUnfinished
 *
 * Returns whether a playbook step is still open on the active path.
 *
 * @param {object|null} task - Mission run task.
 * @returns {boolean} Whether the step is unfinished.
 */
export function isPlaybookFocusStepUnfinished(task) {
  const status = String(task?.status || '').trim();
  return status !== 'complete' && status !== 'skipped';
}

/**
 * isPlaybookFocusStepActionable
 *
 * Returns whether a step is the live process position
 * (ready, in progress, running, blocked, or failed).
 *
 * @param {object|null} task - Mission run task.
 * @returns {boolean} Whether the step is actionable.
 */
export function isPlaybookFocusStepActionable(task) {
  return ACTIONABLE_STEP_STATUSES.has(String(task?.status || '').trim());
}

/**
 * getPlaybookFocusSteps
 *
 * Builds browsable wizard steps from the active playbook path.
 * Skipped alternate branches are excluded.
 *
 * @param {object|null} missionRun - Normalized Mission run.
 * @returns {object[]} Step descriptors in path order.
 */
export function getPlaybookFocusSteps(missionRun) {
  const tasks = getMissionActivePathTasks(missionRun);
  const total = tasks.length;
  let humanStepNumber = 0;

  return tasks.map((task, index) => {
    const isPeopleTask = task?.handledBy !== MISSION_TASK_HANDLED_BY.BOT;
    if (isPeopleTask) {
      humanStepNumber += 1;
    }

    return {
      index,
      position: index + 1,
      stepNumber: isPeopleTask ? humanStepNumber : null,
      task,
      total,
    };
  });
}

/**
 * getPlaybookCurrentStepIndex
 *
 * Resolves the process-current step: first actionable active-path
 * task, else the earliest unfinished task, else the last step.
 *
 * @param {object[]} steps - Focus step descriptors.
 * @returns {number} Current step index, or -1 when empty.
 */
export function getPlaybookCurrentStepIndex(steps) {
  if (!Array.isArray(steps) || steps.length === 0) return -1;

  const actionableIndex = steps.findIndex((step) => (
    isPlaybookFocusStepActionable(step?.task)
  ));
  if (actionableIndex >= 0) return actionableIndex;

  const unfinishedIndex = steps.findIndex((step) => (
    isPlaybookFocusStepUnfinished(step?.task)
  ));
  if (unfinishedIndex >= 0) return unfinishedIndex;

  return steps.length - 1;
}

/**
 * getPlaybookFocusPositionLabel
 *
 * Builds the wizard position sentence for a focused step.
 *
 * @param {object|null} step - Focus step descriptor.
 * @returns {string} Position label.
 */
export function getPlaybookFocusPositionLabel(step) {
  const position = Number(step?.position);
  const total = Number(step?.total);
  if (!Number.isFinite(position) || !Number.isFinite(total) || total < 1) {
    return '';
  }

  const title = String(step?.task?.title || '').trim();
  const prefix = `Step ${position} of ${total}`;
  return title ? `${prefix} · ${title}` : prefix;
}

/**
 * getPlaybookFocusPeekLabel
 *
 * Builds a short previous/next peek label for wizard chrome.
 *
 * @param {object|null} task - Neighbor task.
 * @param {'previous'|'next'} role - Which neighbor is being labeled.
 * @returns {string} Peek label.
 */
export function getPlaybookFocusPeekLabel(task, role) {
  const title = String(task?.title || '').trim() || 'Untitled step';
  if (role !== 'previous') return title;

  const status = String(task?.status || '').trim();
  if (status === 'complete') return `${title} · done`;
  if (status === 'blocked' || status === 'failed') return `${title} · blocked`;
  if (status === 'waiting') return `${title} · waiting`;
  return title;
}
